import { CONNECTION_AUTH_CONFIG, IDENTITY_PROVIDER } from '../config/authConfig';
import type { Region } from '../contexts/RegionContext';

// SCE-11405 Phase 1b: exchange a Federate id_token for temporary IAM credentials via a Cognito
// Identity Pool, exposed as an AwsCredentialIdentityProvider for the Connection Management SDK
// client to SigV4-sign (service=execute-api) requests once the API is flipped to AWS_IAM.
//
// Implemented with plain fetch against the Cognito Identity AWS-JSON-1.1 API (GetId,
// GetCredentialsForIdentity) so the portal takes on no new AWS SDK dependencies. Both calls are
// public (the id_token is the only credential), so no signing is needed to obtain credentials.

interface AwsCredentialIdentity {
  readonly accessKeyId: string;
  readonly secretAccessKey: string;
  readonly sessionToken?: string;
  readonly expiration?: Date;
}

interface CognitoCredentialsResponse {
  Credentials: {
    AccessKeyId: string;
    SecretKey: string;
    SessionToken: string;
    Expiration: number; // epoch seconds
  };
}

// Refresh slightly before actual expiry to avoid using a credential that expires mid-flight.
const EXPIRY_SKEW_MS = 5 * 60 * 1000;

const cognitoEndpoint = (region: string): string => {
  // China regions use the .com.cn suffix; commercial uses .com.
  const suffix = region.startsWith('cn-') ? 'amazonaws.com.cn' : 'amazonaws.com';
  return `https://cognito-identity.${region}.${suffix}/`;
};

const callCognito = async <T>(region: string, target: string, body: unknown): Promise<T> => {
  const response = await fetch(cognitoEndpoint(region), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityService.${target}`,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Cognito ${target} failed (${response.status}): ${text}`);
  }
  return (await response.json()) as T;
};

const getIdentityId = async (region: string, identityPoolId: string, idToken: string): Promise<string> => {
  const result = await callCognito<{ IdentityId: string }>(region, 'GetId', {
    IdentityPoolId: identityPoolId,
    Logins: { [IDENTITY_PROVIDER]: idToken },
  });
  return result.IdentityId;
};

const getCredentials = async (region: string, identityId: string, idToken: string): Promise<AwsCredentialIdentity> => {
  const result = await callCognito<CognitoCredentialsResponse>(region, 'GetCredentialsForIdentity', {
    IdentityId: identityId,
    Logins: { [IDENTITY_PROVIDER]: idToken },
  });
  const c = result.Credentials;
  return {
    accessKeyId: c.AccessKeyId,
    secretAccessKey: c.SecretKey,
    sessionToken: c.SessionToken,
    expiration: new Date(c.Expiration * 1000),
  };
};

// One cache entry per region: the identityId is stable for a given Federate identity, and the
// credentials are reused until they near expiry.
interface CacheEntry {
  identityId?: string;
  credentials?: AwsCredentialIdentity;
}
const cache: Partial<Record<Region, CacheEntry>> = {};

/**
 * Build an AwsCredentialIdentityProvider for the given region + Identity Pool. The returned
 * function is what the SDK client calls on each request; it returns cached credentials until they
 * near expiry, then re-exchanges the (current) Federate id_token for fresh ones.
 *
 * @param region         portal region ('global' | 'china') -- selects the Cognito region + cache slot
 * @param identityPoolId the resolved Cognito Identity Pool id for the active stage
 * @param getToken       async accessor for a valid Federate id_token (e.g. getFederateToken)
 */
export const createCognitoCredentialProvider = (region: Region, identityPoolId: string, getToken: () => Promise<string>) => {
  const { cognitoRegion } = CONNECTION_AUTH_CONFIG[region];

  return async (): Promise<AwsCredentialIdentity> => {
    if (!identityPoolId) {
      throw new Error(`No Cognito Identity Pool configured for region '${region}'`);
    }

    const entry = (cache[region] ??= {});

    const stillValid = entry.credentials?.expiration && entry.credentials.expiration.getTime() - EXPIRY_SKEW_MS > Date.now();
    if (entry.credentials && stillValid) {
      return entry.credentials;
    }

    const idToken = await getToken();
    if (!entry.identityId) {
      entry.identityId = await getIdentityId(cognitoRegion, identityPoolId, idToken);
    }
    entry.credentials = await getCredentials(cognitoRegion, entry.identityId, idToken);
    return entry.credentials;
  };
};
